import React, { useEffect, useRef, useState } from "react";
import {
  fetchPlacement,
  trackAdClick,
  trackAdImpression,
} from "./advertisingApi";
import DisplayAd from "./DisplayAd";
import { isDisplayNetworkItem, detectDeviceClass } from "./providers/AdProviderResolver";
import { placementBackgroundUrl } from "./placementBackground";
import "./AdPlacement.css";

function hasRenderableCreative(creative) {
  if (!creative) return false;
  return ["image_url", "headline", "body", "cta_label"].some(function (field) {
    const value = creative[field];
    return typeof value === "string" && value.trim() !== "";
  });
}

/**
 * Shared placement consumer — request by placement key only.
 */
export default function AdPlacement({
  placementKey,
  className = "",
  variant = "sidebar", // sidebar | banner | compact
  limit = null,
  context = {},
  emptyFallback = null,
  onItems = null,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayFailed, setDisplayFailed] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const tracked = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDisplayFailed(false);
      const ctx = {
        ...context,
        device: context.device || detectDeviceClass(),
      };
      if (limit != null) ctx.limit = limit;
      const result = await fetchPlacement(placementKey, ctx);
      if (cancelled) return;
      const raw = (result && result.items) || [];
      // AdSense auto-ads are sitewide — skip empty network cards without display payload.
      // CatholicAdnet and other display providers include network.display = true.
      const next = raw.filter(function (item) {
        if (!item) return false;
        if (item.provider_kind !== "network") return hasRenderableCreative(item.creative);
        if (item.network && isDisplayNetworkItem(item.network)) return true;
        return false;
      });
      setItems(next);
      setBackgroundUrl(placementBackgroundUrl(result && result.placement));
      setLoading(false);
      if (typeof onItems === "function") onItems(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [placementKey, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    items.forEach((item) => {
      if (item.provider_kind === "network") return;
      const key = `${item.campaign_id}:${item.creative_id || 0}`;
      if (tracked.current[key]) return;
      tracked.current[key] = true;
      trackAdImpression({
        campaign_id: item.campaign_id,
        creative_id: item.creative_id,
        placement_key: placementKey,
      });
    });
  }, [items, placementKey]);

  const handleClick = async (item, event) => {
    const creative = item.creative || {};
    const url = creative.click_url;
    // Must run before any await: the link's default action is decided synchronously.
    if (!url && event) event.preventDefault();
    await trackAdClick({
      campaign_id: item.campaign_id,
      creative_id: item.creative_id,
      placement_key: placementKey,
    });
  };

  const reserveCount = Math.max(
    1,
    limit != null && Number(limit) > 0 ? Math.floor(Number(limit)) : 1
  );

  if (loading) {
    return (
      <div
        className={`ad-placement ad-placement--${variant} ad-placement--loading ${className}`.trim()}
        data-placement={placementKey}
        role="status"
        aria-busy="true"
        aria-live="polite"
      >
        <span className="sr-only">Loading promotions</span>
        {Array.from({ length: reserveCount }).map((_, index) => (
          <div
            key={`${placementKey}-reserve-${index}`}
            className="ad-placement__reserve"
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }
  if (!items.length || displayFailed) {
    return emptyFallback || null;
  }

  return (
    <div
      className={`ad-placement ad-placement--${variant}${backgroundUrl ? " ad-placement--has-background" : ""} ${className}`.trim()}
      data-placement={placementKey}
    >
      {backgroundUrl ? (
        <div
          className="ad-placement__background"
          aria-hidden="true"
          style={{ backgroundImage: `url("${backgroundUrl}")` }}
        />
      ) : null}
      {backgroundUrl ? (
        <div className="ad-placement__background-overlay" aria-hidden="true" />
      ) : null}
      {items.map((item, index) => {
        if (
          item.provider_kind === "network" &&
          item.network &&
          isDisplayNetworkItem(item.network)
        ) {
          return (
            <DisplayAd
              key={`${placementKey}-network-${index}`}
              network={item.network}
              placementKey={placementKey}
              onNoFill={() => setDisplayFailed(true)}
            />
          );
        }

        const creative = item.creative || {};
        const href = creative.click_url || "#";
        const isExternal = href.indexOf("http") === 0;
        return (
          <a
            key={`${item.campaign_id}-${item.creative_id || 0}`}
            className="ad-placement__card"
            href={href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            onClick={(e) => handleClick(item, e)}
          >
            {item.label ? (
              <span className="ad-placement__label">{item.label}</span>
            ) : null}
            {creative.image_url ? (
              <div className="ad-placement__media">
                <img
                  src={creative.image_url}
                  alt={creative.alt_text || creative.headline || "Advertisement"}
                />
              </div>
            ) : null}
            <div className="ad-placement__body">
              {creative.headline ? (
                <strong className="ad-placement__headline">
                  {creative.headline}
                </strong>
              ) : null}
              {creative.body ? (
                <p className="ad-placement__text">{creative.body}</p>
              ) : null}
              {creative.cta_label ? (
                <span className="ad-placement__cta">{creative.cta_label}</span>
              ) : null}
            </div>
          </a>
        );
      })}
    </div>
  );
}
